import React from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Alert,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';
import {
  OrganizationInvitation,
  InvitationStatus,
  getRoleLabel,
  getRoleColor,
} from '../../services/organizationApi';

interface InvitationManagementProps {
  invitations: OrganizationInvitation[];
  onCreateInvitation: () => void;
  canManage: boolean;
  canView: boolean;
}

const InvitationManagement: React.FC<InvitationManagementProps> = ({
  invitations,
  onCreateInvitation,
  canManage,
  canView,
}) => {
  const getStatusIcon = (status: InvitationStatus | string | number) => {
    // Handle string and numeric status values
    const statusValue = typeof status === "string" ? parseInt(status) : status;
    
    switch (statusValue) {
      case InvitationStatus.PENDING:
      case 0:
        return <HourglassIcon color="warning" />;
      case InvitationStatus.ACCEPTED:
      case 1:
        return <CheckCircleIcon color="success" />;
      case InvitationStatus.DECLINED:
      case 2:
        return <CancelIcon color="error" />;
      case InvitationStatus.EXPIRED:
      case 3:
        return <ScheduleIcon color="error" />;
      default:
        return <EmailIcon />;
    }
  };

  const getStatusColor = (status: InvitationStatus | string | number) => {
    // Handle string and numeric status values
    const statusValue = typeof status === "string" ? parseInt(status) : status;
    
    switch (statusValue) {
      case InvitationStatus.PENDING:
      case 0:
        return 'warning';
      case InvitationStatus.ACCEPTED:
      case 1:
        return 'success';
      case InvitationStatus.DECLINED:
      case 2:
        return 'error';
      case InvitationStatus.EXPIRED:
      case 3:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: InvitationStatus | string | number) => {
    // Handle string and numeric status values
    const statusValue = typeof status === "string" ? parseInt(status) : status;
    
    switch (statusValue) {
      case InvitationStatus.PENDING:
      case 0:
        return 'Pending';
      case InvitationStatus.ACCEPTED:
      case 1:
        return 'Accepted';
      case InvitationStatus.DECLINED:
      case 2:
        return 'Declined';
      case InvitationStatus.EXPIRED:
      case 3:
        return 'Expired';
      default:
        return 'Unknown';
    }
  };

  const isExpired = (invitation: OrganizationInvitation): boolean => {
    return new Date(invitation.expires_at) < new Date();
  };

  const getExpiryText = (invitation: OrganizationInvitation): string => {
    const statusValue = typeof invitation.status === "string" ? parseInt(invitation.status) : invitation.status;
    if (statusValue !== InvitationStatus.PENDING && statusValue !== 0) {
      return '';
    }
    
    const expiryDate = new Date(invitation.expires_at);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Expired';
    } else if (diffDays === 0) {
      return 'Expires today';
    } else if (diffDays === 1) {
      return 'Expires tomorrow';
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  const pendingInvitations = invitations.filter(inv => {
    const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
    return statusValue === InvitationStatus.PENDING || statusValue === 0;
  });
  const otherInvitations = invitations.filter(inv => {
    const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
    return statusValue !== InvitationStatus.PENDING && statusValue !== 0;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Organization Invitations ({invitations.length})
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={onCreateInvitation}
          >
            Invite Member
          </Button>
        )}
      </Box>

      {/* Permission Notice */}
      {!canView && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Access Denied:</strong> You don't have permission to view organization invitations.
        </Alert>
      )}
      
      {canView && !canManage && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>View Only:</strong> You can view invitations but only organization owners can send 
          new invitations to members.
        </Alert>
      )}

      {/* Pending Invitations */}
      {canView && pendingInvitations.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, backgroundColor: 'warning.light' }}>
            <Typography variant="h6" color="warning.contrastText">
              Pending Invitations ({pendingInvitations.length})
            </Typography>
          </Box>
          <List>
            {pendingInvitations.map((invitation, index) => (
              <React.Fragment key={invitation.id}>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(invitation.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {invitation.email}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Chip
                            label={`Role: ${getRoleLabel(invitation.role)}`}
                            sx={{
                              backgroundColor: getRoleColor(invitation.role),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                            size="small"
                          />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Invited {new Date(invitation.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color={isExpired(invitation) ? 'error.main' : 'text.secondary'}
                        >
                          {getExpiryText(invitation)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={getStatusLabel(invitation.status)}
                      color={getStatusColor(invitation.status) as any}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </ListItem>
                {index < pendingInvitations.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Other Invitations */}
      {canView && otherInvitations.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, backgroundColor: 'grey.100' }}>
            <Typography variant="h6">
              Other Invitations ({otherInvitations.length})
            </Typography>
          </Box>
          <List>
            {otherInvitations.map((invitation, index) => (
              <React.Fragment key={invitation.id}>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(invitation.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {invitation.email}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Chip
                            label={`Role: ${getRoleLabel(invitation.role)}`}
                            sx={{
                              backgroundColor: getRoleColor(invitation.role),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                            size="small"
                          />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Invited {new Date(invitation.created_at).toLocaleDateString()}
                        </Typography>
                        {(typeof invitation.status === "string" ? parseInt(invitation.status) : invitation.status) === InvitationStatus.ACCEPTED && (
                          <Typography variant="caption" color="success.main">
                            Accepted {new Date(invitation.updated_at || invitation.created_at).toLocaleDateString()}
                          </Typography>
                        )}
                        {(typeof invitation.status === "string" ? parseInt(invitation.status) : invitation.status) === InvitationStatus.DECLINED && (
                          <Typography variant="caption" color="error.main">
                            Declined {new Date(invitation.updated_at || invitation.created_at).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={getStatusLabel(invitation.status)}
                      color={getStatusColor(invitation.status) as any}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </ListItem>
                {index < otherInvitations.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Empty State */}
      {canView && invitations.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Invitations Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start building your organization by inviting new members.
          </Typography>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={onCreateInvitation}
            >
              Send First Invitation
            </Button>
          )}
        </Paper>
      )}

      {/* Invitation Statistics */}
      {invitations.length > 0 && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mt: 3 
        }}>
          <Box>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {pendingInvitations.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {invitations.filter(inv => {
                    const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
                    return statusValue === InvitationStatus.ACCEPTED || statusValue === 1;
                  }).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accepted
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {invitations.filter(inv => {
                    const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
                    return statusValue === InvitationStatus.DECLINED || statusValue === 2;
                  }).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Declined
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {invitations.filter(inv => {
                    const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
                    return statusValue === InvitationStatus.EXPIRED || statusValue === 3;
                  }).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expired
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Invitation Guidelines */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Invitation Guidelines
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2 
        }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              For Organization Owners:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • You can invite new members with any role (except Owner)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Invitations expire after 7 days
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • You can resend expired invitations
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              For Invited Users:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Check your email for invitation details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Accept or decline within 7 days
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Contact the organization owner if you have questions
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default InvitationManagement;
